import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import getPropSelector from './getPropSelector';

// React Native exports a11y prop definitions as TypeScript Interfaces, but no longer exports PropTypes
// so we have to define them ourselves.
const nativeA11yPropTypes = {
  accessible: PropTypes.bool,
  focusable: PropTypes.bool,
  accessibilityElementsHidden: PropTypes.bool,
  accessibilityHint: PropTypes.string,
  // react-native-web ignores `accessibilityHint`
  accessibilityIgnoresInvertColors: PropTypes.bool,
  accessibilityLabel: PropTypes.string,
  accessibilityRole: PropTypes.string,
  accessibilityActions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string
  })),
  accessibilityLiveRegion: PropTypes.oneOf(['none', 'polite', 'assertive']),
  accessibilityState: PropTypes.shape({
    disabled: PropTypes.bool,
    selected: PropTypes.bool,
    checked: PropTypes.oneOf([true, false, 'mixed']),
    busy: PropTypes.bool,
    expanded: PropTypes.bool
  }),
  accessibilityValue: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    now: PropTypes.number,
    text: PropTypes.string
  }),
  accessibilityViewIsModal: PropTypes.bool,
  importantForAccessibility: PropTypes.oneOf(['auto', 'yes', 'no', 'no-hide-descendants']),
  onAccessibilityAction: PropTypes.func,
  onAccessibilityEscape: PropTypes.func,
  onAccessibilityTap: PropTypes.func
};
const a11yPropTypes = {
  ...nativeA11yPropTypes,
  // React Native Web adds many a11y props that alias aria-* attributes
  // Types based on https://necolas.github.io/react-native-web/docs/accessibility/
  accessibilityActiveDescendant: PropTypes.string,
  accessibilityAtomic: PropTypes.bool,
  accessibilityAutoComplete: PropTypes.string,
  accessibilityBusy: PropTypes.bool,
  accessibilityChecked: PropTypes.oneOf([true, false, 'mixed']),
  accessibilityColumnCount: PropTypes.number,
  accessibilityColumnIndex: PropTypes.number,
  accessibilityColumnSpan: PropTypes.number,
  accessibilityControls: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  accessibilityCurrent: PropTypes.oneOf([true, false, 'page', 'step', 'location', 'date', 'time']),
  accessibilityDescribedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  accessibilityDetails: PropTypes.string,
  accessibilityDisabled: PropTypes.bool,
  accessibilityErrorMessage: PropTypes.string,
  accessibilityExpanded: PropTypes.bool,
  accessibilityFlowTo: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  accessibilityHasPopup: PropTypes.string,
  accessibilityHidden: PropTypes.bool,
  accessibilityInvalid: PropTypes.bool,
  accessibilityKeyShortcuts: PropTypes.string,
  accessibilityLabelledBy: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  accessibilityLevel: PropTypes.number,
  accessibilityModal: PropTypes.bool,
  accessibilityMultiline: PropTypes.bool,
  accessibilityMultiSelectable: PropTypes.bool,
  accessibilityOrientation: PropTypes.oneOf(['horizontal', 'vertical']),
  accessibilityOwns: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  accessibilityPlaceholder: PropTypes.string,
  accessibilityPosInSet: PropTypes.number,
  accessibilityPressed: PropTypes.bool,
  accessibilityReadOnly: PropTypes.bool,
  accessibilityRequired: PropTypes.bool,
  accessibilityRoleDescription: PropTypes.string,
  accessibilityRowCount: PropTypes.number,
  accessibilityRowIndex: PropTypes.number,
  accessibilityRowSpan: PropTypes.number,
  accessibilitySelected: PropTypes.bool,
  accessibilitySetSize: PropTypes.number,
  accessibilitySort: PropTypes.oneOf(['ascending', 'descending', 'none', 'other']),
  accessibilityValueMax: PropTypes.number,
  accessibilityValueMin: PropTypes.number,
  accessibilityValueNow: PropTypes.number,
  accessibilityValueText: PropTypes.string
};
const a11yPropTypesByPlatform = Platform.select({
  // React Native Web adds many a11y props that alias aria-* attributes
  // Types based on https://necolas.github.io/react-native-web/docs/accessibility/
  web: a11yPropTypes,
  // Ignore web-only props in native builds
  default: nativeA11yPropTypes
});

// These RNW-only props only exist in RNW >=0.18. Catch them and map them according to platform
// so all props work on RN, RNW >=0.18 and RNW <=0.18, regardless of which they were written for:
// - On native, bundle them into objects, like `accessibilityValue: { max: 100 }`
// - On web, split them into both of:
//   - The appropriate aria-* attr, like `aria-valuenow`, which will work regardless of RNW version
//   - The corresponding RNW >=0.18 prop, like `accessibilityValueNow`, which in some cases does more
//     than just add the aria-* (e.g. `accessibilityDisabled` adds `disabled` if element supports it,
//     and future releases might add more features here).
const rwnPropMappings = {
  // Former accessibilityValue props.
  accessibilityValueMax: value => Platform.select({
    web: {
      'aria-valuemax': value,
      accessibilityValueMax: value
    },
    default: {
      accessibilityValue: {
        max: value
      }
    }
  }),
  accessibilityValueMin: value => Platform.select({
    web: {
      'aria-valuemin': value,
      accessibilityValueMin: value
    },
    default: {
      accessibilityValue: {
        min: value
      }
    }
  }),
  accessibilityValueNow: value => Platform.select({
    web: {
      'aria-valuenow': value,
      accessibilityValueNow: value
    },
    default: {
      accessibilityValue: {
        now: value
      }
    }
  }),
  accessibilityValueText: value => Platform.select({
    web: {
      'aria-valuetext': value,
      accessibilityValueText: value
    },
    default: {
      accessibilityValue: {
        text: value
      }
    }
  }),
  // Former accessibilityState props
  accessibilityBusy: value => Platform.select({
    web: {
      'aria-busy': value,
      accessibilityBusy: value
    },
    default: {
      accessibilityState: {
        busy: value
      }
    }
  }),
  accessibilityChecked: value => Platform.select({
    web: {
      'aria-checked': value,
      accessibilityChecked: value
    },
    default: {
      accessibilityState: {
        checked: value
      }
    }
  }),
  accessibilityDisabled: value => Platform.select({
    web: {
      'aria-disabled': value,
      // RNW >= 0.18 maps `accessibilityDisabled` to `disabled` attr if element supports it
      accessibilityDisabled: value,
      // As of RNW 0.18.9, Pressable doesn't support `accessibilityDisabled`, only `disabled`,
      // but everything else supports `accessibilityDisabled` but not `disabled`.
      disabled: value
    },
    default: {
      accessibilityState: {
        disabled: value
      }
    }
  }),
  accessibilityExpanded: value => Platform.select({
    web: {
      'aria-expanded': value,
      accessibilityExpanded: value
    },
    default: {
      accessibilityState: {
        expanded: value
      }
    }
  }),
  accessibilitySelected: value => Platform.select({
    web: {
      'aria-selected': value,
      accessibilitySelected: value
    },
    default: {
      accessibilityState: {
        selected: value
      }
    }
  })
};
if (Platform.OS === 'web') {
  const mapIfDefined = (value, fn) => value === undefined ? undefined : fn(value);

  // On Web only, these React Native object props need manual mapping in RNW >=0.18
  // which dropped support for the React Native shape of these props.
  // Re-use our RNW 0.18 prop mappings to support both RNW <0.18 (aria-*) and
  // new features added in >=0.18 (e.g. for accessibilityDisabled).
  rwnPropMappings.accessibilityValue = function () {
    let {
      max,
      min,
      now,
      text
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      ...mapIfDefined(max, rwnPropMappings.accessibilityValueMax),
      ...mapIfDefined(min, rwnPropMappings.accessibilityValueMin),
      ...mapIfDefined(now, rwnPropMappings.accessibilityValueNow),
      ...mapIfDefined(text, rwnPropMappings.accessibilityValueText)
    };
  };
  rwnPropMappings.accessibilityState = function () {
    let {
      busy,
      checked,
      disabled,
      expanded,
      selected
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      ...mapIfDefined(busy, rwnPropMappings.accessibilityBusy),
      ...mapIfDefined(checked, rwnPropMappings.accessibilityChecked),
      ...mapIfDefined(disabled, rwnPropMappings.accessibilityDisabled),
      ...mapIfDefined(expanded, rwnPropMappings.accessibilityExpanded),
      ...mapIfDefined(selected, rwnPropMappings.accessibilitySelected)
    };
  };
}
export default {
  /**
   * Proptypes for recognised React Native accessiblity (a11y) props.
   * Spread this in the propTypes of components that accept React Native a11y props.
   */
  types: a11yPropTypes,
  /**
   * Filters a props object, returning only recognised React Native accessiblity (a11y) props.
   *
   * Where components accept React Native a11y props, pass { ...rest } from its props to this,
   * then spread the returned object into the component's props (usually its outer container).
   */
  select: getPropSelector(
  // Allow all React Native accessibility props
  a11yPropTypesByPlatform,
  // Allow any `aria-*` attribute on web; ignore them on native
  Platform.OS === 'web' && /^aria-/,
  // For the props added and deprecated in React Native Web 0.18, convert them to
  // a form that is platform-appropriate and RNW-version safe
  (key, value) => {
    const rnwPropMapper = rwnPropMappings[key];
    return rnwPropMapper ? rnwPropMapper(value) : undefined;
  }),
  /**
   * Use this to disable focus for elements which are visually hidden but still rendered.
   */
  nonFocusableProps: {
    focusable: false,
    // for android, and for keyboard nav in web
    ...Platform.select({
      web: {
        accessibilityHidden: true // web screenreaders
      },
      android: {
        importantForAccessibility: 'no-hide-descendants'
      },
      ios: {
        accessibilityElementsHidden: true
      }
    })
  },
  /**
   * Generates an object of platform-appropriate a11y props describing an item that has an
   * ordered position in a set. Examples of usage by accessibility tools includes screenreaders
   * saying "Item X of Y" when this item is select.
   *
   * @param {number} itemsCount - the number of items in the set
   * @param {number} index - the current item's index in the set
   * @returns {object} - platform-applicable a11y props describing this position (if available)
   */
  getPositionInSet: (itemsCount, index) => Platform.select({
    web: {
      // accessibilityPosInSet etc exists in React Native Web main branch
      // but not in a release compatible with Expo etc; just use `aria-*`
      'aria-setsize': itemsCount,
      'aria-posinset': index + 1
    },
    // No equivalents exist on the native side
    default: {}
  })
};